import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { usePostHog } from '@posthog/react';
import {
  fetchActiveExitSurvey,
  LocalizedExitSurvey,
  surveyDismissedProperties,
  surveySentProperties,
  surveyShownProperties,
} from '../../services/posthog/exitSurvey';

/**
 * Custom-rendered PostHog exit survey (see services/posthog/exitSurvey.ts for
 * why the built-in renderer cannot be used with cookieless mode).
 *
 * Invite-first flow, chosen so the form never takes over a phone screen
 * uninvited: after the survey's configured popup delay a small chat-bubble
 * button appears in the bottom corner; the form only opens when it is tapped.
 * A CTA click before the tap removes the invite — someone who acted is not
 * the "landed and did nothing" audience. `survey shown` is captured only when
 * the form actually opens; the invite's appearance is captured separately as
 * `survey invite shown` so the icon->open->answer funnel is measurable.
 * Nothing is written to the device, so a reload can re-show the invite; that
 * is the accepted cookieless trade-off.
 */

/**
 * Submit-label/placeholder fallbacks per base language. PostHog's API does not
 * currently store survey-level `submitButtonText` translations (it strips the
 * field), so the handful of static UI strings live here; a server-provided
 * translation wins if PostHog starts returning one.
 */
const UI_STRINGS: Record<string, { submit: string; placeholder: string; invite: string }> = {
  en: { submit: 'Submit', placeholder: 'Start typing...', invite: 'Give feedback' },
  it: { submit: 'Invia', placeholder: 'Scrivi qui...', invite: 'Lascia un feedback' },
  de: { submit: 'Absenden', placeholder: 'Hier tippen...', invite: 'Feedback geben' },
  fr: { submit: 'Envoyer', placeholder: 'Écrivez ici...', invite: 'Donner un avis' },
  es: { submit: 'Enviar', placeholder: 'Escribe aquí...', invite: 'Danos tu opinión' },
  nl: { submit: 'Versturen', placeholder: 'Typ hier...', invite: 'Geef feedback' },
};

const THANK_YOU_AUTO_CLOSE_MS = 5000;
const DEFAULT_DELAY_SECONDS = 25;

const shuffle = <T,>(items: T[]): T[] => {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

interface ExitSurveyPopoverProps {
  /** True once the visitor has clicked a phone/online CTA; cancels a pending survey. */
  ctaClicked: boolean;
  locale: string;
}

export const ExitSurveyPopover: FC<ExitSurveyPopoverProps> = ({ ctaClicked, locale }) => {
  const posthog = usePostHog();
  const [localized, setLocalized] = useState<LocalizedExitSurvey | null>(null);
  const [phase, setPhase] = useState<'pending' | 'invite' | 'open' | 'thanks' | 'closed'>(
    'pending',
  );
  const [selected, setSelected] = useState<number[]>([]);
  const [otherText, setOtherText] = useState('');
  // Ref, not state, so the pending timeout sees the latest value without
  // being torn down and restarted by a CTA click.
  const ctaClickedRef = useRef(ctaClicked);
  ctaClickedRef.current = ctaClicked;

  useEffect(() => {
    let cancelled = false;
    fetchActiveExitSurvey(posthog, locale)
      .then((survey) => {
        if (!cancelled && survey) {
          setLocalized(survey);
        }
      })
      // A failed fetch means no survey this page view — never break the page.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [posthog, locale]);

  useEffect(() => {
    if (!localized || phase !== 'pending') {
      return;
    }
    const seconds = localized.survey.appearance?.surveyPopupDelaySeconds ?? DEFAULT_DELAY_SECONDS;
    const timer = setTimeout(() => {
      if (ctaClickedRef.current) {
        setPhase('closed');
        return;
      }
      setPhase('invite');
      posthog.capture('survey invite shown', surveyShownProperties(localized));
    }, seconds * 1000);
    return () => clearTimeout(timer);
  }, [localized, phase, posthog]);

  // A CTA click while the invite is showing withdraws it; an already-open
  // form is left alone.
  useEffect(() => {
    if (ctaClicked && phase === 'invite') {
      setPhase('closed');
    }
  }, [ctaClicked, phase]);

  useEffect(() => {
    if (phase !== 'thanks') {
      return;
    }
    const timer = setTimeout(() => setPhase('closed'), THANK_YOU_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  const question = localized?.survey.questions[0];

  // Shuffled display order, fixed for the lifetime of the popover. The open
  // "Other" choice stays last, as in PostHog's renderer.
  const choiceOrder = useMemo(() => {
    if (!question) {
      return [];
    }
    const indexes = question.choices.map((_, index) => index);
    if (!question.shuffleOptions) {
      return indexes;
    }
    const openIndex = question.hasOpenChoice ? indexes.length - 1 : -1;
    const fixed = shuffle(indexes.filter((index) => index !== openIndex));
    return openIndex === -1 ? fixed : [...fixed, openIndex];
  }, [question]);

  if (!localized || !question || phase === 'pending' || phase === 'closed') {
    return null;
  }

  const { survey, language } = localized;
  const appearance = survey.appearance ?? {};
  const ui = UI_STRINGS[language ?? 'en'] ?? UI_STRINGS.en;
  const openIndex = question.hasOpenChoice ? question.choices.length - 1 : -1;
  const multiSelect = question.type === 'multiple_choice';

  const toggleChoice = (index: number) => {
    setSelected((current) => {
      if (current.includes(index)) {
        return current.filter((value) => value !== index);
      }
      return multiSelect ? [...current, index] : [index];
    });
  };

  const responseValues = selected
    .map((index) => (index === openIndex ? otherText.trim() : question.choices[index]))
    .filter((value) => value.length > 0);
  const canSubmit = responseValues.length > 0;

  const dismiss = () => {
    posthog.capture('survey dismissed', surveyDismissedProperties(localized));
    setPhase('closed');
  };

  const submit = () => {
    if (!canSubmit) {
      return;
    }
    const response = multiSelect ? responseValues : responseValues[0];
    posthog.capture('survey sent', surveySentProperties(localized, response));
    setPhase(appearance.displayThankYouMessage ? 'thanks' : 'closed');
  };

  const openForm = () => {
    setPhase('open');
    posthog.capture('survey shown', surveyShownProperties(localized));
  };

  if (phase === 'invite') {
    return (
      <button
        type="button"
        aria-label={ui.invite}
        title={ui.invite}
        onClick={openForm}
        data-testid="exit-survey-invite"
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          width: 52,
          height: 52,
          borderRadius: '50%',
          border: 'none',
          cursor: 'pointer',
          zIndex: Number(appearance.zIndex) || 2147482647,
          backgroundColor: appearance.submitButtonColor ?? '#1d1f27',
          color: appearance.submitButtonTextColor ?? '#ffffff',
          boxShadow: appearance.boxShadow ?? '0 4px 12px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H9l-4.2 3.6A.5.5 0 0 1 4 19.2V5.5Z"
            fill="currentColor"
          />
        </svg>
      </button>
    );
  }

  const containerStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: 16,
    right: 16,
    maxWidth: 'min(340px, calc(100vw - 32px))',
    width: '100%',
    zIndex: Number(appearance.zIndex) || 2147482647,
    backgroundColor: appearance.backgroundColor ?? '#ffffff',
    color: appearance.textColor ?? '#1d1f27',
    border: `1.5px solid ${appearance.borderColor ?? '#e5e7eb'}`,
    borderRadius: appearance.borderRadius ?? '10px',
    boxShadow: appearance.boxShadow ?? '0 4px 12px rgba(0, 0, 0, 0.15)',
    padding: '20px 24px',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: 1.45,
    boxSizing: 'border-box',
  };

  if (phase === 'thanks') {
    return (
      <div style={containerStyle} role="status" data-testid="exit-survey-thanks">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <strong>{appearance.thankYouMessageHeader ?? 'Thank you for your feedback!'}</strong>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setPhase('closed')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              color: 'inherit',
            }}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={containerStyle}
      role="dialog"
      aria-label={question.question}
      data-testid="exit-survey"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <strong>{question.question}</strong>
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 18,
            color: 'inherit',
          }}
        >
          ×
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {choiceOrder.map((index) => (
          <label
            key={index}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '8px 10px',
              border: `1.5px solid ${selected.includes(index) ? (appearance.submitButtonColor ?? '#1d1f27') : (appearance.borderColor ?? '#e5e7eb')}`,
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            <input
              type={multiSelect ? 'checkbox' : 'radio'}
              name="exit-survey-choice"
              checked={selected.includes(index)}
              onChange={() => toggleChoice(index)}
              style={{ marginTop: 2 }}
            />
            <span>{question.choices[index]}</span>
          </label>
        ))}
        {openIndex !== -1 && selected.includes(openIndex) && (
          <textarea
            rows={2}
            value={otherText}
            onChange={(event) => setOtherText(event.target.value)}
            placeholder={appearance.placeholder ?? ui.placeholder}
            style={{
              backgroundColor: appearance.inputBackground ?? '#f9fafb',
              border: `1.5px solid ${appearance.borderColor ?? '#e5e7eb'}`,
              borderRadius: 6,
              padding: 8,
              font: 'inherit',
              color: 'inherit',
              resize: 'vertical',
            }}
          />
        )}
      </div>
      <button
        type="button"
        onClick={submit}
        disabled={!canSubmit}
        style={{
          marginTop: 14,
          width: '100%',
          padding: '10px 12px',
          border: 'none',
          borderRadius: 6,
          cursor: canSubmit ? 'pointer' : 'default',
          opacity: canSubmit ? 1 : 0.6,
          backgroundColor: appearance.submitButtonColor ?? '#1d1f27',
          color: appearance.submitButtonTextColor ?? '#ffffff',
          font: 'inherit',
          fontWeight: 600,
        }}
      >
        {appearance.submitButtonText ?? ui.submit}
      </button>
    </div>
  );
};
